import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import FormDialog from "../../../../components/common/FormDialog";
import FormField from "../../../../components/common/FormField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import { required } from "../../../../utils/formValidation";
import { DEFAULT_GRANT_TYPES, GRANT_TYPE_OPTIONS } from "../../constants";
import {
  catalogLabels,
  filterByCatalog,
} from "../../../../helpers/catalogFilter.helper";

const schema = {
  name: [required("Client name is required.")],
};

const parseRedirectUris = (text = "") =>
  String(text || "")
    .split(/[\n,]+/)
    .map((uri) => uri.trim())
    .filter(Boolean);

const formatRedirectUris = (uris = []) =>
  (Array.isArray(uris) ? uris : [])
    .map((uri) => String(uri || "").trim())
    .filter(Boolean)
    .join("\n");

const ClientFormDialog = ({
  open,
  mode = "create",
  client = null,
  roles = [],
  rolesLoading = false,
  loading = false,
  showExtendedFields = false,
  showRedirectUrls = false,
  permissionCatalog = false,
  onClose,
  onSubmit,
}) => {
  const isEdit = mode === "edit";
  const showRedirect = Boolean(showExtendedFields || showRedirectUrls);
  const [formError, setFormError] = useState("");
  const [grantType, setGrantType] = useState(DEFAULT_GRANT_TYPES);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [redirectUriText, setRedirectUriText] = useState("");
  const [allowUserCreation, setAllowUserCreation] = useState(!showExtendedFields);
  const [allowSelfUserCreation, setAllowSelfUserCreation] = useState(false);
  const [allowLoginViaOTP, setAllowLoginViaOTP] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const redirectUriTextRef = useRef("");
  const initializedOpenRef = useRef(false);

  const { values, getFieldProps, validateAll, reset } = useFormValidation(
    { name: "" },
    schema
  );

  // Only initialize when the dialog opens — do not reset while the user is typing
  // (e.g. parent re-renders after roles load or silent client refresh).
  useEffect(() => {
    if (!open) {
      initializedOpenRef.current = false;
      return;
    }
    if (initializedOpenRef.current) return;
    initializedOpenRef.current = true;

    setFormError("");

    if (isEdit && client) {
      reset({ name: client.name || "" });
      setGrantType(
        Array.isArray(client.grantType) && client.grantType.length
          ? client.grantType.map(String)
          : DEFAULT_GRANT_TYPES
      );
      setSelectedRoles(
        filterByCatalog(
          Array.isArray(client.roles) ? client.roles : [],
          roles
        )
      );
      const nextRedirect = formatRedirectUris(client.redirectUri);
      redirectUriTextRef.current = nextRedirect;
      setRedirectUriText(nextRedirect);
      setAllowUserCreation(Boolean(client.allowUserCreation));
      setAllowSelfUserCreation(Boolean(client.allowSelfUserCreation));
      setAllowLoginViaOTP(Boolean(client.allowLoginViaOTP));
      setPrivacyPolicy(
        String(
          client.privacyPolicy ||
            client.privacy_policy ||
            client.privacyPolicyUrl ||
            ""
        ).trim()
      );
      return;
    }

    reset({ name: "" });
    setGrantType(DEFAULT_GRANT_TYPES);
    setSelectedRoles([]);
    redirectUriTextRef.current = "";
    setRedirectUriText("");
    setAllowUserCreation(false);
    setAllowSelfUserCreation(false);
    setAllowLoginViaOTP(false);
    setPrivacyPolicy("");
  }, [client, isEdit, open, reset, roles, showExtendedFields]);

  // Drop deleted roles from selection once the live catalog finishes loading.
  useEffect(() => {
    if (!open || rolesLoading) return;
    setSelectedRoles((prev) => filterByCatalog(prev, roles));
  }, [open, roles, rolesLoading]);

  const roleOptions = useMemo(() => {
    if (!permissionCatalog) return catalogLabels(roles);
    const seen = new Set();
    return (Array.isArray(roles) ? roles : [])
      .map((item) => String(typeof item === "string" ? item : item?.name || "").trim())
      .filter((name) => {
        if (!name || seen.has(name)) return false;
        seen.add(name);
        return true;
      });
  }, [permissionCatalog, roles]);

  const handleSubmit = async () => {
    const { isValid, errors } = validateAll();
    if (!isValid) {
      setFormError(errors.name || "Please correct the highlighted fields.");
      return;
    }
    if (!grantType.length) {
      setFormError("Select at least one grant type.");
      return;
    }
    if (!selectedRoles.length) {
      setFormError("Select at least one role.");
      return;
    }

    setFormError("");
    try {
      await onSubmit({
        name: values.name.trim(),
        grantType,
        allowUserCreation,
        allowSelfUserCreation,
        allowLoginViaOTP,
        roles: selectedRoles.map((role) => String(role).trim()).filter(Boolean),
        ...(showRedirect
          ? { redirectUri: parseRedirectUris(redirectUriTextRef.current) }
          : {}),
        ...(showExtendedFields
          ? { privacyPolicy: privacyPolicy.trim() }
          : {}),
      });
    } catch (err) {
      setFormError(
        err?.message ||
          (isEdit ? "Unable to update client." : "Unable to create client.")
      );
    }
  };

  const canSubmit = Boolean(
    values.name?.trim() &&
      grantType.length > 0 &&
      selectedRoles.length > 0 &&
      !loading
  );

  return (
    <FormDialog
      open={open}
      title={isEdit ? "Edit Client" : "Add Client"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      disableSubmit={!canSubmit}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel={isEdit ? "Save Changes" : "Create Client"}
      loadingLabel={isEdit ? "Saving..." : "Creating..."}
    >
      <FormField label="Client Name" required {...getFieldProps("name")} />
      <ChipAutocomplete
        label="Grant Type"
        required
        options={GRANT_TYPE_OPTIONS}
        value={grantType}
        onChange={setGrantType}
        disabled={loading}
      />
      <ChipAutocomplete
        label="Roles"
        required
        options={roleOptions}
        value={selectedRoles}
        onChange={setSelectedRoles}
        loading={rolesLoading}
        disabled={loading}
      />
      {showRedirect ? (
        <FormField
          label="Redirect URLs"
          value={redirectUriText}
          onChange={(event) => {
            const next = event.target.value;
            redirectUriTextRef.current = next;
            setRedirectUriText(next);
          }}
          disabled={loading}
          multiline
          rows={2}
          placeholder="https://example.com/callback"
          helperText="One URL per line (or comma-separated)."
          InputLabelProps={{ shrink: true }}
        />
      ) : null}

      <FormGroup sx={{ gap: 0.5 }}>
        <FormControlLabel
          sx={{ ml: 0, alignItems: "center" }}
          control={
            <Checkbox
              checked={allowUserCreation}
              onChange={(event) => setAllowUserCreation(event.target.checked)}
              disabled={loading}
            />
          }
          label="Allow User Creation"
        />
        {showExtendedFields ? (
          <>
            <FormControlLabel
              sx={{ ml: 0, alignItems: "center" }}
              control={
                <Checkbox
                  checked={allowSelfUserCreation}
                  onChange={(event) =>
                    setAllowSelfUserCreation(event.target.checked)
                  }
                  disabled={loading}
                />
              }
              label="Allow Self User Registration"
            />
            <FormControlLabel
              sx={{ ml: 0, alignItems: "center" }}
              control={
                <Checkbox
                  checked={allowLoginViaOTP}
                  onChange={(event) => setAllowLoginViaOTP(event.target.checked)}
                  disabled={loading}
                />
              }
              label="Allow Login via OTP"
            />
          </>
        ) : null}
      </FormGroup>

      {showExtendedFields ? (
        <FormField
          label="Privacy Policy"
          value={privacyPolicy}
          onChange={(event) => setPrivacyPolicy(event.target.value)}
          disabled={loading}
          helperText="Optional. Clear the field and save to remove the privacy policy URL."
          InputLabelProps={{ shrink: true }}
        />
      ) : null}
    </FormDialog>
  );
};

export default ClientFormDialog;
